import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Check, Eye, Send, Trash2, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ExpenseClaim } from '../services/expenseClaimsService';

type ExpenseClaimsListProps = {
  title?: string;
  claims: ExpenseClaim[];
  loading?: boolean;
  withoutContainer?: boolean;
  showReviewActions?: boolean;
  showReviewSummaryAction?: boolean;
  onSubmit?: (claim: ExpenseClaim) => void;
  onDelete?: (claim: ExpenseClaim) => void;
  onApprove?: (claim: ExpenseClaim) => void;
  onReject?: (claim: ExpenseClaim) => void;
  onViewDetails?: (claim: ExpenseClaim) => void;
  onOpenReviewSummary?: (claim: ExpenseClaim) => void;
};

const statusVariantMap: Record<ExpenseClaim['status'], 'secondary' | 'default' | 'destructive'> = {
  DRAFT: 'secondary',
  SUBMITTED: 'default',
  APPROVED: 'default',
  REJECTED: 'destructive',
};

export const ExpenseClaimsList: React.FC<ExpenseClaimsListProps> = ({
  title,
  claims,
  loading = false,
  withoutContainer = false,
  showReviewActions = false,
  showReviewSummaryAction = false,
  onSubmit,
  onDelete,
  onApprove,
  onReject,
  onViewDetails,
  onOpenReviewSummary,
}) => {
  const { t } = useTranslation();

  const content = (
    <>
        {loading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
        ) : claims.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('expenseClaims.list.empty', 'No expense claims found.')}
          </p>
        ) : (
          <div className="space-y-3">
            {claims.map((claim) => (
              <div key={claim.id} className="rounded-lg border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium">#{claim.id}</p>
                    <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">
                      <p className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" />
                        <span>{claim.date.slice(0, 10)}</span>
                      </p>
                      <p>
                        <span className="font-medium text-foreground">EUR {Number(claim.amount).toFixed(2)}</span>
                      </p>
                    </div>
                  </div>

                  <div className="shrink-0">
                    <Badge variant={statusVariantMap[claim.status]}>
                      {t(
                        `expenseClaims.status.${claim.status.toLowerCase()}`,
                        claim.status,
                      )}
                    </Badge>
                  </div>
                </div>

                {(claim.notes || claim.user?.name) && (
                  <div className="mt-3 space-y-1">
                    {claim.notes && (
                      <p className="text-sm">
                        <span className="text-muted-foreground">{t('expenseClaims.list.reasonLabel', 'Motivazione')}:</span>{' '}
                        <span>{claim.notes}</span>
                      </p>
                    )}
                    {claim.user?.name && (
                      <p className="text-xs text-muted-foreground">
                        {t('expenseClaims.list.userLabel', 'User')}: {claim.user.name}
                      </p>
                    )}
                  </div>
                )}

                {((!showReviewActions && claim.status === 'DRAFT' && Boolean(onSubmit)) ||
                  (!showReviewActions && ['DRAFT', 'REJECTED'].includes(claim.status) && Boolean(onDelete)) ||
                  Boolean(onViewDetails) ||
                  (showReviewActions && claim.status === 'SUBMITTED' && Boolean(onApprove)) ||
                  (showReviewActions && claim.status === 'SUBMITTED' && Boolean(onReject)) ||
                  (showReviewSummaryAction && claim.status === 'SUBMITTED' && Boolean(onOpenReviewSummary))) && (
                  <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t pt-3">
                    {onViewDetails && (
                    <Button size="sm" variant="outline" onClick={() => onViewDetails(claim)}>
                      <Eye className="h-4 w-4 mr-1" />
                      {t('common.details', 'Dettagli')}
                    </Button>
                    )}
                    {!showReviewActions && claim.status === 'DRAFT' && onSubmit && (
                    <Button size="sm" onClick={() => onSubmit(claim)}>
                      <Send className="h-4 w-4 mr-1" />
                      {t('expenseClaims.actions.submit', 'Submit')}
                    </Button>
                    )}
                    {!showReviewActions && ['DRAFT', 'REJECTED'].includes(claim.status) && onDelete && (
                    <Button size="sm" variant="outline" onClick={() => onDelete(claim)}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('common.delete', 'Delete')}
                    </Button>
                    )}

                    {showReviewActions && claim.status === 'SUBMITTED' && onApprove && (
                    <Button size="sm" onClick={() => onApprove(claim)}>
                      <Check className="h-4 w-4 mr-1" />
                      {t('expenseClaims.actions.approve', 'Approve')}
                    </Button>
                    )}
                    {showReviewActions && claim.status === 'SUBMITTED' && onReject && (
                    <Button size="sm" variant="destructive" onClick={() => onReject(claim)}>
                      <X className="h-4 w-4 mr-1" />
                      {t('expenseClaims.actions.reject', 'Reject')}
                    </Button>
                    )}

                    {showReviewSummaryAction && claim.status === 'SUBMITTED' && onOpenReviewSummary && (
                    <Button size="sm" onClick={() => onOpenReviewSummary(claim)}>
                      <Check className="h-4 w-4 mr-1" />
                      {t('expenseClaims.actions.approve', 'Approve')}
                    </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </>
  );

  if (withoutContainer) {
    return content;
  }

  return (
    <Card>
      {title && (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent>{content}</CardContent>
    </Card>
  );
};
